import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import AuthService from "../service/AuthService";
import MessageBox from "sap/m/MessageBox";
import Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import Dialog from "sap/m/Dialog";
import MultiInput from "sap/m/MultiInput";
import Token from "sap/m/Token";
import SelectDialog from "sap/m/SelectDialog";
import StandardListItem from "sap/m/StandardListItem";
import EventBus from "sap/ui/core/EventBus";
import Input from "sap/m/Input";
import Label from "sap/m/Label";
import CheckBox from "sap/m/CheckBox";
import IconTabBar from "sap/m/IconTabBar";
import IconTabFilter from "sap/m/IconTabFilter";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import Title from "sap/m/Title";
import type {
	CreateUserPayload,
	UpdateUserPayload,
	UserLookupItem,
	UserRecord,
} from "../service/UsersService";
import UsersService from "../service/UsersService";
import PluginService from "../service/PluginService";
import type { PluginInfo } from "../service/PluginService";
import UserPluginVarsService from "../service/UserPluginVarsService";
import PluginI18nService from "../service/PluginI18nService";
import ValueHelp from "../util/ValueHelp";
import type {
	PluginVariableValueHelpOption,
	UserPluginVariablesGroup,
} from "../service/UserPluginVarsService";

const SECRET_SENTINEL = "••••••••";

const SYSTEM_ADMIN_ROLE = "system:admin";

/** A list row, with the flags the action buttons bind to:
 * - `isSelf` — your own row; the backend refuses deactivate and delete on it.
 * - `isSystemAdmin` — hides edit, and hides delete from all but the startup admin.
 * - `isRootAdmin` — the startup admin's row, which nobody may delete. */
type UsersPageRow = UserRecord & {
	isSelf: boolean;
	isSystemAdmin: boolean;
	isRootAdmin: boolean;
};

interface UsersPageModel {
	items: UsersPageRow[];
	busy: boolean;
	lookups: {
		roles: UserLookupItem[];
		permissions: UserLookupItem[];
		groups: UserLookupItem[];
	};
	/** True when the logged-in account is the startup system admin. */
	viewerIsRootAdmin: boolean;
	dialogTitle: string;
	dialogMode: "create" | "edit";
	isDialogOpen: boolean;
	isEditMode: boolean;
	passwordRequired: boolean;
}

interface UsersEditModel {
	id: number | null;
	email: string;
	password: string;
	name: string;
	companyName: string;
	roles: string[];
	permissions: string[];
	groups: string[];
}

function createLookupItems(entries: string[]): UserLookupItem[] {
	return entries.map((entry) => ({ key: entry, text: entry }));
}

function createEmptyEditModel(): UsersEditModel {
	return {
		id: null,
		email: "",
		password: "",
		name: "",
		companyName: "",
		roles: [],
		permissions: [],
		groups: [],
	};
}

type UsersPageJsonModel = JSONModel & {
	getData(): UsersPageModel;
	setData(data: UsersPageModel): void;
	getProperty(path: string): unknown;
	setProperty(path: string, value: unknown): void;
};

type UsersEditJsonModel = JSONModel & {
	getData(): UsersEditModel;
	setData(data: UsersEditModel): void;
	getProperty(path: string): unknown;
	setProperty(path: string, value: unknown): void;
};

/**
 * @namespace com.ang.portal.controller
 */
export default class Users extends BaseController {
	private dialogOpenLock = false;
	private _pluginVarGroups: UserPluginVariablesGroup[] = [];

	public onInit(): void {
		this.setModel(
			new JSONModel({
				items: [],
				busy: false,
				lookups: {
					roles: [],
					permissions: [],
					groups: [],
				},
				viewerIsRootAdmin: false,
				dialogTitle: "",
				dialogMode: "create",
				isDialogOpen: false,
				isEditMode: false,
				passwordRequired: true,
				actionsEnabled: true,
			}),
			"usersPage",
		);
		this.setModel(new JSONModel(createEmptyEditModel()), "usersEdit");

		this.refreshPermissions();
		const canRead = this.getPageModel().getProperty("/canRead") as boolean;

		if (AuthService.isAuthenticated() && canRead) {
			void this.loadUsersPage();
		}

		EventBus.getInstance().subscribe(
			"PluginsChannel",
			"PluginsChanged",
			this._onPluginsChanged,
			this,
		);
	}

	public onExit(): void {
		EventBus.getInstance().unsubscribe(
			"PluginsChannel",
			"PluginsChanged",
			this._onPluginsChanged,
			this,
		);
	}

	private _onPluginsChanged(): void {
		// Only refresh the plugin lookup silently; do not reload the user list,
		// and never surface a toast — the user is acting on the Plugins page.
		const canRead = this.getPageModel().getProperty("/canRead") as boolean;
		if (!AuthService.isAuthenticated() || !canRead) {
			return;
		}
		void this.refreshLookupsSilently();
	}

	private async refreshLookupsSilently(): Promise<void> {
		try {
			const lookups = await UsersService.getLookups();
			this.getPageModel().setProperty("/lookups", {
				roles: createLookupItems(lookups.roles),
				permissions: createLookupItems(lookups.permissions),
				groups: createLookupItems(lookups.groups),
			});
		} catch {
			// Lookup refresh on plugin change is best-effort; ignore failures.
		}
	}

	public refreshPage(): void {
		this.refreshPermissions();
		const canRead = this.getPageModel().getProperty("/canRead") as boolean;
		if (AuthService.isAuthenticated() && canRead) {
			void this.loadUsersPage();
		}
	}

	private refreshPermissions(): void {
		const userInfo = AuthService.getUserInfo();
		const roles = userInfo?.roles ?? [];
		const permissions = userInfo?.permissions ?? [];
		const isSystemAdmin = roles.includes("system:admin");
		const isUsersAdmin = roles.includes("users:admin");

		this.getPageModel().setProperty(
			"/canCreate",
			isSystemAdmin || isUsersAdmin || permissions.includes("users:create"),
		);
		this.getPageModel().setProperty(
			"/canRead",
			isSystemAdmin || isUsersAdmin || permissions.includes("users:read"),
		);
		this.getPageModel().setProperty(
			"/canUpdate",
			isSystemAdmin || isUsersAdmin || permissions.includes("users:update"),
		);
		this.getPageModel().setProperty(
			"/canDelete",
			isSystemAdmin || isUsersAdmin || permissions.includes("users:delete"),
		);
		// Only the startup admin may delete a system admin account, so only it is shown
		// that action on those rows. The backend enforces this independently.
		this.getPageModel().setProperty(
			"/viewerIsRootAdmin",
			userInfo?.isRootAdmin === true,
		);
	}

	private getPageModel(): UsersPageJsonModel {
		return this.getModel("usersPage") as UsersPageJsonModel;
	}

	private getEditModel(): UsersEditJsonModel {
		return this.getModel("usersEdit") as UsersEditJsonModel;
	}

	private getUserFromEvent(oEvent: Event): UserRecord | null {
		const source = oEvent.getSource();
		if (!(source instanceof Control)) {
			return null;
		}

		const rawUser = source
			.getBindingContext("usersPage")
			?.getObject() as unknown;
		if (!rawUser || typeof rawUser !== "object") {
			return null;
		}

		return rawUser as UserRecord;
	}

	private async loadUsersPage(): Promise<void> {
		const pageModel = this.getPageModel();
		pageModel.setProperty("/busy", true);

		try {
			// The lookups are reserved for system:admin/users:admin, while the list only
			// needs `users:read` — so a read-only viewer gets a 403 there. They feed the
			// create/edit pickers, which such a viewer cannot open anyway, so a failure
			// must not take the list down with it.
			const [items, lookups] = await Promise.all([
				UsersService.getUsers(),
				UsersService.getLookups().catch((): null => null),
			]);

			// A system admin sees the whole list, other system admins included. Lower roles
			// have no authority over those accounts, so for them the list omits them —
			// except their own row, which is always shown.
			const userInfo = AuthService.getUserInfo();
			const currentUserId = userInfo?.id;
			const viewerIsSystemAdmin = (userInfo?.roles ?? []).includes(
				SYSTEM_ADMIN_ROLE,
			);
			const filteredItems: UsersPageRow[] = items
				.map((u) => ({
					...u,
					isSelf: u.id === currentUserId,
					// Trust the server's flag: `roles` only lists directly assigned roles, so
					// an account holding system:admin through user_roles or a group — the
					// startup admin included — reads as an ordinary user here.
					isSystemAdmin:
						u.is_system_admin ??
						(u.roles ?? []).includes(SYSTEM_ADMIN_ROLE),
					isRootAdmin: u.is_root_admin === true,
				}))
				.filter((u) => viewerIsSystemAdmin || u.isSelf || !u.isSystemAdmin);

			pageModel.setProperty("/items", filteredItems);
			if (lookups) {
				pageModel.setProperty("/lookups", {
					roles: createLookupItems(lookups.roles),
					permissions: createLookupItems(lookups.permissions),
					groups: createLookupItems(lookups.groups),
				});
			}
		} catch (error) {
			await this.showToastForError(error, "usersLoadError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	private openDialog(mode: "create" | "edit", user?: UserRecord): void {
		const pageModel = this.getPageModel();
		const dialog = this.byId("userDialog") as Dialog;
		const dialogOpenState = pageModel.getProperty("/isDialogOpen") === true;
		const isDialogOpen =
			typeof (dialog as unknown as { isOpen?: () => boolean }).isOpen ===
			"function"
				? (dialog as unknown as { isOpen: () => boolean }).isOpen()
				: false;

		if (this.dialogOpenLock || dialogOpenState || isDialogOpen) {
			return;
		}
		this.dialogOpenLock = true;

		const editModel = this.getEditModel();

		if (mode === "create") {
			editModel.setData(createEmptyEditModel());
			pageModel.setProperty("/passwordRequired", true);
			pageModel.setProperty("/isEditMode", false);
			void this.loadPluginVariablesForCreate();
		} else if (user) {
			editModel.setData({
				id: user.id,
				email: user.email,
				password: "",
				name: user.name ?? "",
				companyName:
					(user as UserRecord & { company_name?: string }).company_name ?? "",
				roles: [...user.roles],
				permissions: [...user.permissions],
				groups: [...(user.groups ?? [])],
			});
			pageModel.setProperty("/passwordRequired", false);
			pageModel.setProperty("/isEditMode", true);
			void this.loadPluginVariables(user.id);
		}

		pageModel.setProperty("/dialogMode", mode);
		pageModel.setProperty("/isDialogOpen", true);
		pageModel.setProperty("/actionsEnabled", false);
		void this.getResourceBundle().then((bundle) => {
			pageModel.setProperty(
				"/dialogTitle",
				bundle.getText(
					mode === "create" ? "usersDialogCreateTitle" : "usersDialogEditTitle",
				),
			);
		});

		const editData = editModel.getData() as UsersEditModel;
		this.syncTokensToMultiInput("userRolesInput", editData.roles);
		this.syncTokensToMultiInput("userPermissionsInput", editData.permissions);
		this.syncTokensToMultiInput("userGroupsInput", editData.groups);

		dialog.open();
	}

	public onOpenCreateDialog(): void {
		this.openDialog("create");
	}

	public onEditUserPress(oEvent: Event): void {
		const user = this.getUserFromEvent(oEvent);
		if (user) {
			this.openDialog("edit", user);
		}
	}

	public onDialogAfterClose(): void {
		const pageModel = this.getPageModel();
		pageModel.setProperty("/isDialogOpen", false);
		pageModel.setProperty("/actionsEnabled", true);
		this.dialogOpenLock = false;
	}

	public onDialogAfterOpen(): void {
		this.dialogOpenLock = false;
	}

	public onCloseDialog(): void {
		const pageModel = this.getPageModel();
		pageModel.setProperty("/isDialogOpen", false);
		pageModel.setProperty("/actionsEnabled", true);
		(this.byId("userDialog") as Dialog).close();
	}

	public async onSaveUser(): Promise<void> {
		const pageModel = this.getPageModel();
		const editModel = this.getEditModel();
		const dialogMode = pageModel.getProperty(
			"/dialogMode",
		) as UsersPageModel["dialogMode"];
		const data = editModel.getData() as UsersEditModel;

		if (!data.email.trim()) {
			await this.showToast("usersValidationRequired");
			return;
		}

		if (dialogMode === "create" && !data.password) {
			await this.showToast("usersValidationPasswordRequired");
			return;
		}

		const roles = this.getTokenKeys("userRolesInput");
		const permissions = this.getTokenKeys("userPermissionsInput");
		const groups = this.getTokenKeys("userGroupsInput");

		pageModel.setProperty("/busy", true);
		try {
			if (dialogMode === "create") {
				const payload: CreateUserPayload = {
					email: data.email.trim().toLowerCase(),
					password: data.password,
					...(data.name.trim() ? { name: data.name.trim() } : {}),
					...(data.companyName.trim()
						? { company_name: data.companyName.trim() }
						: {}),
					roles,
					permissions,
					groups,
				};

				const created = await UsersService.createUser(payload);
				if (created?.id) {
					await this.savePluginVariables(created.id);
				}
				await this.showToast("usersCreateSuccess");
			} else if (data.id !== null) {
				const payload: UpdateUserPayload = {
					email: data.email.trim().toLowerCase(),
					...(data.name.trim() ? { name: data.name.trim() } : {}),
					...(data.companyName.trim()
						? { company_name: data.companyName.trim() }
						: {}),
					roles,
					permissions,
					groups,
					...(data.password ? { password: data.password } : {}),
				};

				const result = await UsersService.updateUser(data.id, payload);
				if (result.accessToken && result.refreshToken) {
					AuthService.storeTokens({
						accessToken: result.accessToken,
						refreshToken: result.refreshToken,
						user: result.user,
					});
				} else {
					const currentUser = AuthService.getUserInfo();
					if (currentUser?.id === result.user.id) {
						AuthService.updateStoredUser(result.user);
					}
				}
				await this.savePluginVariables(data.id);
				await this.showToast("usersUpdateSuccess");
			}

			this.onCloseDialog();
			await this.loadUsersPage();
		} catch (error) {
			await this.showToastForError(
				error,
				dialogMode === "create" ? "usersCreateError" : "usersUpdateError",
			);
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	public async onDeleteUserPress(oEvent: Event): Promise<void> {
		const user = this.getUserFromEvent(oEvent);
		if (!user) {
			return;
		}

		const bundle = await this.getResourceBundle();
		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(bundle.getText("usersDeleteConfirm", [user.email]), {
				actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
				emphasizedAction: MessageBox.Action.OK,
				onClose: (action) => resolve(action === "OK"),
			});
		});

		if (!confirmed) {
			return;
		}

		const pageModel = this.getPageModel();
		pageModel.setProperty("/busy", true);
		try {
			await UsersService.deleteUser(user.id);
			await this.showToast("usersDeleteSuccess");
			await this.loadUsersPage();
		} catch (error) {
			await this.showToastForError(error, "usersDeleteError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	public async onToggleActivePress(oEvent: Event): Promise<void> {
		const user = this.getUserFromEvent(oEvent);
		if (!user) {
			return;
		}

		const isCurrentlyActive = user.is_active !== false;
		const bundle = await this.getResourceBundle();
		const confirmMsg = isCurrentlyActive
			? bundle.getText("usersDeactivateConfirm", [user.email])
			: bundle.getText("usersActivateConfirm", [user.email]);

		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(confirmMsg, {
				actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
				emphasizedAction: MessageBox.Action.OK,
				onClose: (action) => resolve(action === "OK"),
			});
		});

		if (!confirmed) {
			return;
		}

		const pageModel = this.getPageModel();
		pageModel.setProperty("/busy", true);
		try {
			await UsersService.toggleActive(user.id);
			const toastKey = isCurrentlyActive
				? "usersDeactivateSuccess"
				: "usersActivateSuccess";
			await this.showToast(toastKey);
			await this.loadUsersPage();
		} catch (error) {
			await this.showToastForError(error, "usersToggleActiveError");
		} finally {
			pageModel.setProperty("/busy", false);
		}
	}

	// --- Value Help ---

	private syncTokensToMultiInput(inputId: string, keys: string[]): void {
		const oInput = this.byId(inputId) as MultiInput;
		oInput.removeAllTokens();
		keys.forEach((key) => {
			oInput.addToken(new Token({ key, text: key }));
		});
	}

	private getTokenKeys(inputId: string): string[] {
		const oInput = this.byId(inputId) as MultiInput;
		return oInput.getTokens().map((token) => token.getKey());
	}

	private openValueHelpDialog(
		inputId: string,
		lookupPath: string,
		titleKey: string,
	): void {
		const oInput = this.byId(inputId) as MultiInput;
		const pageModel = this.getPageModel();
		const lookups = pageModel.getProperty(lookupPath) as UserLookupItem[];

		void this.getResourceBundle().then((bundle) => {
			ValueHelp.openMultiSelect({
				title: bundle.getText(titleKey),
				entries: lookups,
				selectedKeys: oInput.getTokens().map((token) => token.getKey()),
				onConfirm: (keys) => {
					oInput.removeAllTokens();
					keys.forEach((key) => {
						const match = lookups.find((item) => item.key === key);
						oInput.addToken(
							new Token({ key, text: match ? match.text : key }),
						);
					});
				},
			});
		});
	}

	private async openPluginVariableValueHelp(
		pluginId: string,
		variableKey: string,
		valueHelp: { path: string; title?: string; multi?: boolean },
		fallbackTitle: string,
	): Promise<void> {
		const options = await UserPluginVarsService.getValueHelpOptions(
			pluginId,
			valueHelp.path,
		);
		const model = this.getModel("userPluginVars") as JSONModel | undefined;
		if (!model) {
			return;
		}

		const currentValue = model.getProperty(`/${pluginId}/${variableKey}`) as
			| string
			| undefined;
		const title = valueHelp.title || fallbackTitle;
		await this.openSingleSelectDialog(
			title,
			options,
			currentValue || "",
			(selectedKey) => {
				model.setProperty(`/${pluginId}/${variableKey}`, selectedKey);
			},
			valueHelp.multi === true,
		);
	}

	/**
	 * Value help for a plugin variable. With `multi`, several entries can be
	 * picked and the value is stored as a comma-separated list ("C1240,C1135") -
	 * plugin variables are a single text column, so there is no array to store.
	 * Backends that accept multiple values split on that delimiter.
	 */
	private async openSingleSelectDialog(
		title: string,
		options: PluginVariableValueHelpOption[],
		selectedKey: string,
		onConfirm: (selectedKey: string) => void,
		multi = false,
	): Promise<void> {
		// Pre-tick whatever is already saved, so opening the dialog to add one
		// more code does not silently drop the existing ones on confirm.
		const preselected = new Set(
			selectedKey
				.split(",")
				.map((part) => part.trim())
				.filter((part) => part.length > 0),
		);
		options = options.map((option) => ({
			...option,
			selected: preselected.has(option.key),
		})) as PluginVariableValueHelpOption[];

		const valueHelpModel = new JSONModel({
			items: options,
		});
		const applyFilter = (query: string) => {
			const normalizedQuery = query.trim().toLowerCase();
			if (!normalizedQuery) {
				valueHelpModel.setProperty("/items", options);
				return;
			}

			const filtered = options.filter((option) => {
				const haystack = [option.key, option.text, option.description]
					.filter(
						(part): part is string =>
							typeof part === "string" && part.length > 0,
					)
					.join(" ")
					.toLowerCase();
				return haystack.includes(normalizedQuery);
			});
			valueHelpModel.setProperty("/items", filtered);
		};

		const oDialog = new SelectDialog({
			title,
			multiSelect: multi,
			contentHeight: "20rem",
			search: (oEvent: Event) => {
				applyFilter(
					(oEvent.getParameter("value" as never) as string | undefined) || "",
				);
			},
			liveChange: (oEvent: Event) => {
				applyFilter(
					(oEvent.getParameter("value" as never) as string | undefined) || "",
				);
			},
			confirm: (oEvent: Event) => {
				const keyOf = (item: StandardListItem | undefined) =>
					String(
						item?.getBindingContext("valueHelp")?.getProperty("key") || "",
					);

				if (multi) {
					const selectedItems = (oEvent.getParameter(
						"selectedItems" as never,
					) as StandardListItem[] | undefined) || [];
					// Clearing every entry confirms an empty value, which is a
					// legitimate way to unset the variable.
					onConfirm(selectedItems.map(keyOf).filter(Boolean).join(","));
					return;
				}

				const selectedItem = oEvent.getParameter("selectedItem" as never) as
					| StandardListItem
					| undefined;
				if (selectedItem) {
					onConfirm(keyOf(selectedItem));
				}
				oDialog.destroy();
			},
			// One dialog per request, so it has to clean up after itself.
			cancel: () => {
				oDialog.destroy();
			},
		});
		oDialog.setModel(valueHelpModel, "valueHelp");
		oDialog.bindAggregation("items", {
			path: "valueHelp>/items",
			template: new StandardListItem({
				title: "{valueHelp>key}",
				description:
					"{= ${valueHelp>text} !== ${valueHelp>key} ? ${valueHelp>text} : ${valueHelp>description} }",
				// Precomputed above rather than compared inline, so a comma
				// separated multi-value pre-ticks every entry it names.
				selected: "{valueHelp>selected}",
			} as any),
		});
		oDialog.open("");
	}

	public onRolesValueHelp(): void {
		this.openValueHelpDialog(
			"userRolesInput",
			"/lookups/roles",
			"usersValueHelpRoles",
		);
	}

	public onPermissionsValueHelp(): void {
		this.openValueHelpDialog(
			"userPermissionsInput",
			"/lookups/permissions",
			"usersValueHelpPermissions",
		);
	}

	public onRolesTokenUpdate(oEvent: Event): void {
		this.handleTokenUpdate(oEvent);
	}

	public onPermissionsTokenUpdate(oEvent: Event): void {
		this.handleTokenUpdate(oEvent);
	}

	public onGroupsValueHelp(): void {
		this.openValueHelpDialog(
			"userGroupsInput",
			"/lookups/groups",
			"usersValueHelpGroups",
		);
	}

	public onGroupsTokenUpdate(oEvent: Event): void {
		this.handleTokenUpdate(oEvent);
	}

	private handleTokenUpdate(oEvent: Event): void {
		const type = oEvent.getParameter("type" as never) as string;
		if (type === "removed") {
			const removedTokens: Token[] = oEvent.getParameter(
				"removedTokens" as never,
			);
			const oInput: MultiInput = oEvent.getSource();
			removedTokens.forEach((token) => {
				oInput.removeToken(token);
			});
		}
	}

	// --- Plugin Variables ---

	private clearPluginVariables(): void {
		this._pluginVarGroups = [];
		const tabs = this.byId("userDialogTabs") as IconTabBar | undefined;
		if (tabs) {
			// Remove all dynamic plugin tabs (keep the static "details" tab).
			const items = tabs.getItems().slice();
			for (const item of items) {
				if (item instanceof IconTabFilter && item.getKey() !== "details") {
					tabs.removeItem(item);
					item.destroy();
				}
			}
			tabs.setSelectedKey("details");
		}
		const model = this.getModel("userPluginVars");
		if (model) (model as JSONModel).setData({});
	}

	private async loadPluginVariables(userId: number): Promise<void> {
		this.clearPluginVariables();
		try {
			const groups = await UserPluginVarsService.getForUser(userId);
			this._pluginVarGroups = groups;
			const data: Record<string, Record<string, string | boolean>> = {};
			for (const group of groups) {
				data[group.pluginId] = {};
				for (const v of group.variables) {
					data[group.pluginId][v.key] =
						v.type === "boolean" ? v.value === "true" : (v.value ?? "");
				}
			}
			this.setModel(new JSONModel(data), "userPluginVars");
			await this.renderPluginVariables(groups);
		} catch {
			await this.showToast("usersPluginVariablesLoadError");
		}
	}

	private async loadPluginVariablesForCreate(): Promise<void> {
		this.clearPluginVariables();
		try {
			const plugins = await PluginService.getPlugins();
			const groups: UserPluginVariablesGroup[] = plugins
				.filter(
					(p: PluginInfo) =>
						Array.isArray(p.userVariables) && p.userVariables.length > 0,
				)
				.map((p: PluginInfo) => ({
					pluginId: p.id,
					pluginName: p.name,
					pluginDisplayName: p.displayName,
					variables: (p.userVariables ?? []).map((v) => ({
						key: v.key,
						label: v.label,
						description: v.description,
						group: v.group,
						type: v.type,
						required: !!v.required,
						secret: !!v.secret,
						valueHelp: v.valueHelp,
						isSet: false,
						value: v.default ?? "",
					})),
				}));
			this._pluginVarGroups = groups;
			const data: Record<string, Record<string, string | boolean>> = {};
			for (const group of groups) {
				data[group.pluginId] = {};
				for (const v of group.variables) {
					data[group.pluginId][v.key] =
						v.type === "boolean" ? v.value === "true" : (v.value ?? "");
				}
			}
			this.setModel(new JSONModel(data), "userPluginVars");
			await this.renderPluginVariables(groups);
		} catch {
			await this.showToast("usersPluginVariablesLoadError");
		}
	}

	private async renderPluginVariables(
		groups: UserPluginVariablesGroup[],
	): Promise<void> {
		const tabs = this.byId("userDialogTabs") as IconTabBar | undefined;
		if (!tabs) return;
		if (groups.length === 0) return;
		const bundle = await this.getResourceBundle();
		for (const group of groups) {
			// Variable labels, descriptions, section titles and value-help titles may
			// be "{i18n>key}" references into the plugin's own bundle; literals pass
			// through unchanged.
			const t = await PluginI18nService.getResolver(group.pluginId, [
				group.pluginDisplayName,
				...group.variables.flatMap((v) => [
					v.label,
					v.description,
					v.group,
					v.valueHelp?.title,
				]),
			]);
			const formContent: Control[] = [];
			let currentVariableGroup = "";
			for (const v of group.variables) {
				const variableGroup = v.group || "";
				if (variableGroup && variableGroup !== currentVariableGroup) {
					formContent.push(new Title({ text: t(variableGroup) }));
					currentVariableGroup = variableGroup;
				}

				const resolvedLabel = t(v.label, v.key);
				const resolvedDescription = t(v.description);
				const labelText = v.required
					? bundle.getText("usersPluginVariableRequired", [resolvedLabel])
					: resolvedLabel;
				formContent.push(
					new Label({ text: labelText, required: !!v.required }),
				);

				if (v.type === "boolean") {
					formContent.push(
						new CheckBox({
							text: resolvedDescription || labelText,
							selected: `{userPluginVars>/${group.pluginId}/${v.key}}`,
						}),
					);
					continue;
				}

				const inputType = v.secret ? "Password" : v.type === "email" ? "Email" : "Text";
				const inputSettings: Record<string, unknown> = {
					value: `{userPluginVars>/${group.pluginId}/${v.key}}`,
					type: inputType,
					placeholder:
						v.secret && v.isSet ? SECRET_SENTINEL : resolvedDescription,
				};

				if (v.valueHelp) {
					inputSettings.showValueHelp = true;
					inputSettings.valueHelpOnly = true;
					const valueHelp = {
						...v.valueHelp,
						title: t(v.valueHelp.title),
					};
					inputSettings.valueHelpRequest = () => {
						void this.openPluginVariableValueHelp(
							group.pluginId,
							v.key,
							valueHelp,
							resolvedLabel,
						).catch(async (error) => {
							await this.showToastForError(
								error,
								"usersPluginVariableValueHelpError",
							);
						});
					};
				}

				const input = new Input(inputSettings as any);
				formContent.push(input);
			}
			const form = new SimpleForm({
				editable: true,
				layout: "ResponsiveGridLayout",
				labelSpanXL: 4,
				labelSpanL: 4,
				labelSpanM: 4,
				labelSpanS: 12,
				emptySpanXL: 0,
				emptySpanL: 0,
				emptySpanM: 0,
				emptySpanS: 0,
				columnsXL: 1,
				columnsL: 1,
				columnsM: 1,
				content: formContent,
			});
			const tab = new IconTabFilter({
				key: `plugin-${group.pluginId}`,
				text: t(group.pluginDisplayName, group.pluginName),
				content: [form],
			});
			tabs.addItem(tab);
		}
	}

	private collectPluginVariableUpdates(): Array<{
		pluginId: string;
		values: Record<string, string | null>;
	}> {
		const model = this.getModel("userPluginVars") as JSONModel | undefined;
		if (!model) return [];
		const data = model.getData() as Record<
			string,
			Record<string, string | boolean>
		>;
		const result: Array<{
			pluginId: string;
			values: Record<string, string | null>;
		}> = [];
		for (const group of this._pluginVarGroups) {
			const values: Record<string, string | null> = {};
			let hasUpdate = false;
			const pluginData = data[group.pluginId] ?? {};
			for (const v of group.variables) {
				const rawNewValue = pluginData[v.key] ?? "";
				const newValue =
					v.type === "boolean"
						? rawNewValue === true
							? "true"
							: ""
						: String(rawNewValue);
				const original = v.value ?? "";
				if (v.secret) {
					// Skip when unchanged sentinel — let backend keep current encrypted value.
					if (newValue === SECRET_SENTINEL || newValue === original) continue;
					if (newValue === "") {
						values[v.key] = null;
					} else {
						values[v.key] = newValue;
					}
					hasUpdate = true;
				} else {
					if (newValue === original) continue;
					values[v.key] = newValue === "" ? null : newValue;
					hasUpdate = true;
				}
			}
			if (hasUpdate) result.push({ pluginId: group.pluginId, values });
		}
		return result;
	}

	private async savePluginVariables(userId: number): Promise<void> {
		const updates = this.collectPluginVariableUpdates();
		for (const u of updates) {
			await UserPluginVarsService.update(userId, u.pluginId, u.values);
		}
	}
}
